import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  LabelList,
} from 'recharts';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { useAnalyticsContext } from '../analytics-context';

const COLORS = [
  '#2563eb',
  '#0ea5e9',
  '#22c55e',
  '#f97316',
  '#8b5cf6',
  '#ec4899',
  '#14b8a6',
  '#facc15',
];

const GaCountryChart: React.FC = () => {
  const { ga, isCustomRangePending } = useAnalyticsContext();
  const demographicsError = ga.demographics.error as any;

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const chartData = useMemo(() => {
    return ga.demographics.countries
      .slice(0, 8)
      .map((country, index) => ({
        name: country.country,
        users: country.users,
        color: COLORS[index % COLORS.length],
        gradientId: `countryGradient-${slugify(country.country || `_${index}`)}`,
      }))
      .filter(item => item.users > 0);
  }, [ga.demographics.countries]);

  if (!ga.integration.isConfigured) {
    return (
      <Card className="border-none shadow-sm h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Top Countries
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
          {ga.integration.message ||
            'Connect Google Analytics to view audience geography.'}
        </CardContent>
      </Card>
    );
  }

  if (demographicsError && demographicsError.status && demographicsError.status !== 400) {
    return (
      <Card className="border-none shadow-sm h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Top Countries
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[280px] items-center justify-center text-sm text-red-600">
          {demographicsError.data?.message ||
            demographicsError.message ||
            'Failed to load country data.'}
        </CardContent>
      </Card>
    );
  }

  if (ga.demographics.isLoading) {
    return (
      <Card className="border-none shadow-sm h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Top Countries
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[280px] items-center justify-center">
          <div className="h-48 w-full animate-pulse rounded-xl bg-muted/50" />
        </CardContent>
      </Card>
    );
  }

  if (isCustomRangePending) {
    return (
      <Card className="border-none shadow-sm h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Top Countries
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
          Select a start and end date to view Google Analytics insights.
        </CardContent>
      </Card>
    );
  }

  if (!chartData.length) {
    return (
      <Card className="border-none shadow-sm h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Top Countries
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
          No country data available for this period.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-none shadow-sm h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold">
          Top Countries
        </CardTitle>
      </CardHeader>
      <CardContent className="h-[280px]">
        <ResponsiveContainer>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 10, right: 32, left: 0, bottom: 10 }}
          >
            <defs>
              {chartData.map(item => (
                <linearGradient
                  key={item.name}
                  id={item.gradientId}
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="0"
                >
                  <stop offset="0%" stopColor={item.color} stopOpacity={0.85} />
                  <stop offset="100%" stopColor={item.color} stopOpacity={0.55} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid
              strokeDasharray="4 4"
              className="stroke-muted/50"
              horizontal={false}
            />
            <XAxis
              type="number"
              tickMargin={8}
              axisLine={false}
              tickLine={false}
              className="text-xs text-muted-foreground"
              tickFormatter={value =>
                value.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })
              }
            />
            <YAxis
              dataKey="name"
              type="category"
              tickLine={false}
              axisLine={false}
              className="text-xs text-muted-foreground"
              width={130}
            />
            <Tooltip
              cursor={{ fill: 'rgba(37, 99, 235, 0.08)' }}
              formatter={(value: number) => [
                value.toLocaleString(),
                'Users',
              ]}
            />
            <Bar
              dataKey="users"
              radius={[0, 10, 10, 0]}
              isAnimationActive
              animationDuration={900}
            >
              <LabelList
                dataKey="users"
                position="right"
                className="text-xs font-medium text-foreground"
                formatter={(value: number) => value.toLocaleString()}
              />
              {chartData.map(item => (
                <Cell
                  key={item.name}
                  fill={`url(#${item.gradientId})`}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default GaCountryChart;

